import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Sparkle, LogOut, Shield } from "lucide-react";
import ThemeToggle from "./ThemeToggle";
import VerifyEmailBanner from "./VerifyEmailBanner";

const navItems = [
    { to: "/dashboard", label: "Recommendations", testid: "nav-recommendations" },
    { to: "/world", label: "My World", testid: "nav-world" },
    { to: "/trips", label: "Saved Trips", testid: "nav-trips" },
    { to: "/profile", label: "Profile", testid: "nav-profile" },
];

export default function Layout({ children }) {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const handleLogout = async () => {
        await logout();
        navigate("/login");
    };

    return (
        <div className="min-h-screen bg-sand-50 text-ink">
            <header className="border-b border-ink/10 bg-sand-50/85 backdrop-blur sticky top-0 z-30">
                <div className="sp-container flex items-center justify-between h-20">
                    <Link to="/dashboard" className="flex items-center gap-3" data-testid="brand-link">
                        <Sparkle strokeWidth={1.25} className="w-5 h-5 text-planet-sun" />
                        <span className="font-serif text-2xl tracking-tight">StarPath</span>
                    </Link>
                    <nav className="hidden md:flex items-center gap-8">
                        {navItems.map((it) => (
                            <NavLink
                                key={it.to}
                                to={it.to}
                                data-testid={it.testid}
                                className={({ isActive }) =>
                                    "text-sm tracking-wide transition-colors " +
                                    (isActive ? "text-ink" : "text-ink-muted hover:text-ink")
                                }
                            >
                                {it.label}
                            </NavLink>
                        ))}
                    </nav>
                    <div className="flex items-center gap-3 sm:gap-4">
                        {user?.is_admin && (
                            <Link to="/admin" className="hidden sm:flex items-center gap-1.5 text-xs text-ink-muted hover:text-ink transition-colors px-3 py-1.5 border border-ink/10 rounded-full" data-testid="nav-admin">
                                <Shield strokeWidth={1.5} className="w-3.5 h-3.5" />
                                Admin
                            </Link>
                        )}
                        {user && user.name && (
                            <span className="hidden sm:inline text-sm text-ink-muted" data-testid="header-user-name">
                                {user.name.split(" ")[0]}
                            </span>
                        )}
                        <ThemeToggle />
                        <button
                            onClick={handleLogout}
                            className="text-ink-muted hover:text-ink transition-colors p-2"
                            data-testid="logout-button"
                            aria-label="Logout"
                        >
                            <LogOut strokeWidth={1.5} className="w-4 h-4" />
                        </button>
                    </div>
                </div>
                <div className="md:hidden border-t border-ink/10">
                    <div className="sp-container flex items-center gap-6 py-3 overflow-x-auto">
                        {navItems.map((it) => (
                            <NavLink
                                key={it.to}
                                to={it.to}
                                data-testid={`mobile-${it.testid}`}
                                className={({ isActive }) =>
                                    "text-xs tracking-wide whitespace-nowrap " +
                                    (isActive ? "text-ink" : "text-ink-muted")
                                }
                            >
                                {it.label}
                            </NavLink>
                        ))}
                    </div>
                </div>
            </header>
            <VerifyEmailBanner />
            <main className="animate-fade-up">{children}</main>
            <footer className="mt-24 border-t border-ink/10">
                <div className="sp-container py-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs text-ink-muted">
                    <div>© {new Date().getFullYear()} StarPath. A quiet way to travel by the stars.</div>
                    <div className="sp-overline">Made with celestial care</div>
                </div>
            </footer>
        </div>
    );
}
